import { Eye, Folder, Globe, Lock, MinusCircle, Smartphone } from 'lucide-react'

import { cn } from '../../components/cn'
import { t } from '../../copy'

/**
 * 作用域标签（设计稿 SPEC-components §1）。
 *
 * 硬约束 01：东西存到哪儿，必须一眼看见。图标必配文字——
 * 不靠颜色区分，三种图标形状不同，读屏也读得出。
 *
 * 高 24px · 内距 0 8px · 圆角 6px。杂项 globe，项目 folder，
 * 离线待上传 phone（作用域写「本机」，不写「已保存」）。
 */
export function ScopeLabel(props: {
  kind: 'misc' | { project: string } | 'local'
  className?: string
  'data-testid'?: string
}) {
  const projectName =
    props.kind !== 'local' && props.kind !== 'misc' ? props.kind.project : ''
  const local = props.kind === 'local'
  const misc = props.kind === 'misc'

  const Icon = local ? Smartphone : misc ? Globe : Folder
  const text = local
    ? t('memo.scope.local')
    : misc
      ? t('memo.scope.misc')
      : projectName

  return (
    <span
      data-testid={props['data-testid']}
      className={cn(
        'inline-flex h-[24px] shrink-0 items-center gap-[5px] rounded-[6px] px-[8px] text-[14px] font-medium',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
        local
          ? 'bg-m-wn-s text-m-wn-t'
          : misc
            ? 'bg-m-bg3 text-m-t3'
            : 'bg-m-bg4 text-m-t1',
        props.className,
      )}
    >
      <Icon size={13} strokeWidth={1.8} aria-hidden />
      {text}
    </span>
  )
}

/**
 * AI 三态的图标 + 文案（SPEC-scope-ai §2.4）。
 *
 * default 灰减号（不是告警），shared 绿眼睛（必须带范围名），
 * hidden 中性灰锁（不用红色——是用户的选择，不是故障）。
 */
export function AiAccessBadge(props: {
  access: 'default' | 'shared' | 'hidden'
  /** shared 时必须给出范围名：「AI 可读 —— 只有全局 Lulu」/「AI 可读 —— 这个项目的 …」。 */
  sharedLabel?: string
  className?: string
  'data-testid'?: string
}) {
  const { access } = props
  const label =
    access === 'shared' ? (props.sharedLabel ?? t('memo.ai.shared')) : t(`memo.ai.${access}`)

  return (
    <span
      data-testid={props['data-testid']}
      className={cn(
        'inline-flex shrink-0 items-center gap-[5px] text-[11px]',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
        access === 'shared' ? 'text-m-ok-t' : 'text-m-t3',
        props.className,
      )}
      title={label}
    >
      <AiAccessIcon access={access} />
      {label}
    </span>
  )
}

export function AiAccessIcon(props: { access: 'default' | 'shared' | 'hidden' }) {
  if (props.access === 'shared') {
    return <Eye size={13} strokeWidth={1.8} aria-hidden />
  }
  if (props.access === 'hidden') {
    return <Lock size={13} strokeWidth={1.8} aria-hidden />
  }
  return <MinusCircle size={13} strokeWidth={1.8} aria-hidden />
}
